import React, {
	useEffect,
	useState,
	useCallback,
	useMemo,
	useLayoutEffect,
} from "react";
import {
	Box,
	Typography,
	CircularProgress,
	List,
	Divider,
	Tabs,
	Tab,
	TextField,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	InputAdornment,
	useMediaQuery,
	useTheme,
	ListItemButton,
	ListItem,
	Avatar,
	SwipeableDrawer,
	Toolbar,
	Menu,
	MenuItem,
	Tooltip,
	FormControl,
	InputLabel,
	Select,
} from "@mui/material";
import {
	ChevronLeft,
	ChevronRight,
	Search as SearchIcon,
	DirectionsCar,
	Moving,
	WifiOff,
	ElectricRickshaw,
	HourglassFull,
	EvStation,
	Storefront,
	Dashboard as DashboardIcon,
	Route as RouteIcon,
	Straighten,
	Timeline,
	List as ListIcon,
	CloudOutlined,
	Terminal,
	WarningAmber,
	InfoOutline,
	AssignmentOutlined,
	Menu as MenuIcon,
	FilterList,
	Close as CloseIcon,
	Memory,
	Insights as InsightsIcon,
	OpenInNew,
} from "@mui/icons-material";
import "react-day-picker/dist/style.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CANData from "./CANData";
import Distance from "./Distance";
import Commands from "./Commands";
import Logs from "./Logs";
import Route from "./Route";
import History from "./History";
import MQTT from "./MQTT";
import Alerts from "./Alerts";
import Dashboard from "./Dashboard/Dashboard";
import { VehicleDataProvider, useVehicleData } from "../../DataContext";
import Overview from "./Overview";
import VehicleInsights from "./Insights";
import useFleet from "../../hooks/useFleet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

const TAB_BAR_WIDTH = 128; // px — matches your compact vertical tabs

const toSlug = (s = "") =>
	s.toString().toLowerCase().trim().replace(/\s/g, "-");

// put near other constants
const STATUS_ICON = {
	IDLE: HourglassFull,
	MOVING: Moving,
	CHARGING: EvStation,
	OFFLINE: WifiOff,
};

const STATUS_COLOR = {
	IDLE: "#f39c12",
	MOVING: "#2980b9",
	CHARGING: "#27ae60",
	OFFLINE: "#c0392b",
};

const FILTERS = ["ALL", "IDLE", "MOVING", "CHARGING", "OFFLINE"];

const filterIconFor = (f, sx) => {
	const I = f === "ALL" ? ElectricRickshaw : STATUS_ICON[f] || WifiOff;
	const color =
		f === "ALL" ? "inherit" : STATUS_COLOR[f] || STATUS_COLOR.OFFLINE;
	return <I sx={{ color, fontSize: 18, ...sx }} />;
};
const filterLabel = (f) => f[0] + f.slice(1).toLowerCase();

function StatusIcon({ status, sx }) {
	const S = STATUS_ICON[status] || WifiOff;
	const color = STATUS_COLOR[status] || STATUS_COLOR.OFFLINE;
	return <S sx={{ color, fontSize: 12, flexShrink: 0, ...sx }} />;
}

const TAB_META = {
	Dashboard: { icon: DashboardIcon, color: "#16a085" },
	CAN: { icon: Memory, color: "#27ae60" },
	Alerts: { icon: WarningAmber, color: "#c0392b" },
	History: { icon: Timeline, color: "#2980b9" },
	Logs: { icon: ListIcon, color: "#8e44ad" },
	Overview: { icon: InfoOutline, color: "#2c3e50" },
	Details: { icon: AssignmentOutlined, color: "#f39c12" },
	Distance: { icon: Straighten, color: "#d35400" },
	Route: { icon: RouteIcon, color: "#16a085" },
	MQTT: { icon: CloudOutlined, color: "#27ae60" },
	Commands: { icon: Terminal, color: "#2980b9" },
	Insights: { icon: InsightsIcon, color: "#1abc9c" },
};

function TabLabel({ name, selected }) {
	const meta = TAB_META[name] || {};
	const Ico = meta.icon || InfoOutline;
	const color = meta.color || (selected ? "#1976d2" : "inherit");
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			<Ico sx={{ fontSize: 16, color }} />
			<span>{name}</span>
		</Box>
	);
}

function SyncSelectedVehicle({ value }) {
	const { setSelectedVehicle } = useVehicleData();

	useEffect(() => {
		setSelectedVehicle(value);
	}, [value, setSelectedVehicle]);
	return null;
}

const norm = (s) => (s ?? "").toString().toLowerCase();

export const vehicleMatches = (v, term) => {
	const t = norm(term);
	if (!t) return true;
	return [
		// ids
		v.ch_no,
		v.dev_id,
		// dealer info
		v.dlr_nm,
		v.ct,
		v.stt,
	]
		.map(norm)
		.join(" ")
		.includes(t);
};

const formatDealerName = (dealer) => {
	return `${dealer.name} • ${dealer.city}, ${dealer.state} (${dealer.vehicle_count})`;
};

// put with other constants
const STATUS_ORDER = { IDLE: 0, MOVING: 1, CHARGING: 2, OFFLINE: 3 };

const Vehicles = ({ maps }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const location = useLocation();
	const navigate = useNavigate();
	const { vehicleId: vehicleIdParam } = useParams();

	const [selectedVehicleId, setSelectedVehicleId] = useState(null);
	const [vehicleData, setVehicleData] = useState(null);
	const [loadingDetails, setLoadingDetails] = useState(false);
	const [selectedTab, setSelectedTab] = useState(0);
	const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchMode, setSearchMode] = useState(false);
	const [mobileTabsOpen, setMobileTabsOpen] = useState(false);
	const [statusFilter, setStatusFilter] = useState("ALL");
	const [filterAnchor, setFilterAnchor] = useState(null);
	const [searchFocused, setSearchFocused] = useState(false);
	const [dealers, setDealers] = useState([]); // [{id, name}]
	const [dealerId, setDealerId] = useState("ALL"); // "ALL" | dealer.id
	const [pinSelected, setPinSelected] = useState(false);
	const [primaryView, setPrimaryView] = useState("vehicles");
	const [selectedDealerId, setSelectedDealerId] = useState(null);
	const [dealerSearchTerm, setDealerSearchTerm] = useState("");
	const { vehiclesMap, orderedIds, counts } = useFleet();
	const [loadingVehicles] = useState(false); // list comes from hook

	useEffect(() => {
		setPinSelected(false);
	}, [statusFilter, dealerId, searchTerm]);

	const openFilter = (e) => setFilterAnchor(e.currentTarget);
	const closeFilter = () => setFilterAnchor(null);
	const pickFilter = (value) => {
		setStatusFilter(value);
		closeFilter();
	};

	useEffect(() => {
		if (!orderedIds.length) return;

		if (!location.hash && vehicleData?.tabs?.length) {
			const defaultTab = vehicleData.tabs[0]?.name || "Dashboard";
			navigate(`${location.pathname}#${encodeURIComponent(defaultTab)}`, {
				replace: true,
			});
		}
	}, [orderedIds, vehicleIdParam, vehicleData?.tabs, navigate, location]);

	const [detailsReloadKey, setDetailsReloadKey] = useState(0);

	const handlePrimaryViewChange = useCallback(
		(_, value) => {
			if (!value) return;
			setPrimaryView(value);
			if (value === "dealers") {
				setSelectedVehicleId(null);
				setVehicleData(null);
				setLoadingDetails(false);
			}
		},
		[setLoadingDetails, setSelectedVehicleId, setVehicleData]
	);

	const makeSelectHandler = useCallback(
		(id) => () => {
			if (id == null) return;
			const nextId = String(id);

			// optimistic select so effects see the new id immediately
			if (String(selectedVehicleId) === nextId) {
				setDetailsReloadKey((k) => k + 1); // force refetch if same id clicked
			} else {
				setSelectedVehicleId(nextId);
			}

			setLoadingDetails(true);
			setVehicleData(null); // clear stale data

			const tabName =
				vehicleData?.tabs?.[selectedTab]?.name || "Dashboard";
			navigate(
				`/vehicles/${encodeURIComponent(nextId)}#${encodeURIComponent(
					tabName
				)}`
			);
			setSearchMode(false);
		},
		[
			navigate,
			selectedTab,
			vehicleData?.tabs,
			selectedVehicleId,
			setSearchMode,
		]
	);

	// ADD once inside component (top-level, not inside JSX):
	const renderFilterMenu = (anchorEl, onClose) => (
		<Menu
			anchorEl={anchorEl}
			open={Boolean(anchorEl)}
			onClose={onClose}
			keepMounted
			PaperProps={{ sx: (t) => ({ zIndex: t.zIndex.modal + 2 }) }}
		>
			{FILTERS.map((f) => (
				<MenuItem
					key={f}
					selected={statusFilter === f}
					onClick={() => {
						pickFilter(f);
					}}
					dense
					sx={{
						gap: 1,
						minWidth: 220,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{filterIconFor(f, { fontSize: 18 })}
						<Typography variant="body2">
							{filterLabel(f)}
						</Typography>
					</Box>
					<Typography
						variant="caption"
						sx={{ color: "text.secondary" }}
					>
						{countsLegend[f] ?? 0}
					</Typography>
				</MenuItem>
			))}
		</Menu>
	);

	const selectedVehicle = useMemo(
		() =>
			selectedVehicleId ? vehiclesMap[selectedVehicleId] || null : null,
		[vehiclesMap, selectedVehicleId]
	);

	useEffect(() => {
		// lock background scroll when search overlay is open
		const prev = document.body.style.overflow;
		if (searchMode) document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [searchMode]);

	useEffect(() => {
		setSidebarOpen(!isMobile);
	}, [isMobile]);

	const isDealerView = primaryView === "dealers";

	useEffect(() => {
		if (!isDealerView) {
			setSelectedDealerId(null);
			setDealerSearchTerm("");
			return;
		}
		setSearchMode(false);
	}, [isDealerView]);

	const byDealer = useCallback(
		(v) => {
			if (dealerId === "ALL" || dealerId === "" || dealerId == null)
				return true;

			const selId = String(dealerId);

			// prefer numeric/string id match from vehicle
			const vId =
				v.dealer_id != null
					? String(v.dealer_id)
					: v.dealer?.id != null
					? String(v.dealer.id)
					: "";

			if (vId && vId === selId) return true;

			// fallback by dealer name if ids are missing
			const sel = dealers.find((x) => String(x._id) === selId);
			if (!sel) return false;

			const lhs = (v.dealer_name || v.dlr_nm || "").trim().toLowerCase();
			const rhs = (sel.name || "").trim().toLowerCase(); // <- was _name
			return lhs && rhs && lhs === rhs;
		},
		[dealerId, dealers]
	);

	const filteredVehicles = useMemo(() => {
		// hydrate each entry with its map key as `id`
		let list = orderedIds
			.map((id) => ({ id, ...(vehiclesMap[id] || {}) }))
			.filter((v) => v && v.id != null);

		const norm = (s) => (s ?? "").toString().toLowerCase();
		const t = norm(searchTerm);

		list = list.filter(byDealer);
		if (t) list = list.filter((v) => vehicleMatches(v, t));
		if (statusFilter !== "ALL")
			list = list.filter((v) => v.st === statusFilter);

		// sort by status then chassis
		list = list.sort(
			(a, b) =>
				(STATUS_ORDER[a.st ?? "OFFLINE"] ?? 4) -
					(STATUS_ORDER[b.st ?? "OFFLINE"] ?? 4) ||
				String(a.ch_no ?? "").localeCompare(String(b.ch_no ?? ""))
		);

		// pin selected (uses id safely)
		const activeTabName = vehicleData?.tabs?.[selectedTab]?.name || "";
		const shouldPinSelected = !searchFocused && activeTabName !== "Route";
		if (shouldPinSelected && selectedVehicleId) {
			const selId = String(selectedVehicleId);
			const idx = list.findIndex((v) => String(v.id) === selId);
			if (idx > 0) {
				const sel = list[idx];
				list = [sel, ...list.slice(0, idx), ...list.slice(idx + 1)];
			} else if (idx === -1 && pinSelected && vehiclesMap[selId]) {
				list = [{ id: selId, ...vehiclesMap[selId] }, ...list];
			}
		}

		return list;
	}, [
		vehiclesMap,
		searchTerm,
		selectedVehicleId,
		statusFilter,
		orderedIds,
		searchFocused,
		selectedTab,
		vehicleData,
		pinSelected,
		byDealer,
	]);

	const searchActive = (searchTerm ?? "").trim().length > 0;

	const listByDealerAndSearch = useMemo(() => {
		const base = orderedIds
			.map((id) => ({ id, ...(vehiclesMap[id] || {}) }))
			.filter((v) => v && v.id != null)
			.filter(byDealer);

		if (!searchActive) return base;
		return base.filter((v) => vehicleMatches(v, searchTerm));
	}, [orderedIds, vehiclesMap, byDealer, searchActive, searchTerm]);

	const countsForUI = useMemo(() => {
		if (!searchActive) return counts; // use backend/global
		const acc = { ALL: 0, IDLE: 0, MOVING: 0, CHARGING: 0, OFFLINE: 0 };
		for (const v of listByDealerAndSearch) {
			acc.ALL++;
			if (acc[v.st] != null) acc[v.st]++; // st ∈ {IDLE,MOVING,CHARGING,OFFLINE}
		}
		return acc;
	}, [searchActive, listByDealerAndSearch, counts]);

	const countsForDealerOnly = useMemo(() => {
		// counts when ONLY dealer filter changes (no search)
		const acc = { ALL: 0, IDLE: 0, MOVING: 0, CHARGING: 0, OFFLINE: 0 };
		const list = orderedIds
			.map((id) => vehiclesMap[id])
			.filter(Boolean)
			.filter(byDealer);

		for (const v of list) {
			acc.ALL++;
			if (acc[v.st] != null) acc[v.st]++;
		}
		return acc;
	}, [orderedIds, vehiclesMap, byDealer]);

	const filteredDealers = useMemo(() => {
		const term = dealerSearchTerm.trim().toLowerCase();
		const entries = dealers
			.map((dealer) => ({
				...dealer,
				loc:
					dealer.latitude != null && dealer.longitude != null
						? {
								lat: Number(dealer.latitude),
								lng: Number(dealer.longitude),
						  }
						: null,
			}))
			.sort((a, b) => String(a.name).localeCompare(String(b.name)));

		if (!term) return entries;
		return entries.filter((dealer) =>
			[dealer.name, dealer.city, dealer.state]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
				.includes(term)
		);
	}, [dealers, dealerSearchTerm]);

	const countsLegend = useMemo(() => {
		const dealerActive = !(
			dealerId === "ALL" ||
			dealerId === "" ||
			dealerId == null
		);
		if (searchActive) return countsForUI; // dealer + search handled inside countsForUI
		return dealerActive ? countsForDealerOnly : counts; // dealer-only vs global backend
	}, [searchActive, countsForUI, countsForDealerOnly, counts, dealerId]);

	// fetch dealers
	useEffect(() => {
		const url = new URL("dealers/", BACKEND_URL).toString();
		fetch(url, { headers: { Authorization: `Bearer ${BACKEND_TOKEN}` } })
			.then((r) => r.json())
			.then((d) => {
				if (!d?.success) return;
				const raw = Array.isArray(d.data)
					? d.data
					: Array.isArray(d.data?.list)
					? d.data.list
					: [];
				const normalized = raw.map((dealer) => ({
					...dealer,
					_id: dealer.id,
					_display: formatDealerName(dealer),
				}));
				setDealers(normalized);
				// if current selection no longer exists, reset to ALL
				if (
					dealerId !== "ALL" &&
					!normalized.some((x) => x._id === dealerId)
				) {
					setDealerId("ALL");
				}
			})
			.catch(() => {});
	}, [dealerId]);

	const renderPrimaryTabs = (variant = "desktop") => (
		<Tabs
			value={primaryView}
			onChange={handlePrimaryViewChange}
			variant="fullWidth"
			textColor="primary"
			indicatorColor="primary"
			sx={{
				minHeight: variant === "mobile" ? 38 : 36,
				mt: variant === "mobile" ? 0 : 1,
				"& .MuiTab-root": {
					textTransform: "none",
					fontWeight: 600,
					minHeight: variant === "mobile" ? 38 : 36,
				},
			}}
		>
			<Tab
				value="vehicles"
				disableRipple
				label={`Vehicles (${filteredVehicles.length})`}
			/>
			<Tab
				value="dealers"
				disableRipple
				label={`Dealers (${filteredDealers.length})`}
			/>
		</Tabs>
	);

	const handleDealerSelect = useCallback(
		(id) => () => {
			if (id == null) return;
			setSelectedDealerId(String(id));
			setSearchMode(false);
		},
		[setSearchMode]
	);


	useEffect(() => {
		if (
			!vehicleIdParam ||
			vehicleIdParam === "undefined" ||
			vehicleIdParam === "null"
		) {
			setSelectedVehicleId(null);
			return;
		}
		const nextId = String(vehicleIdParam);
		if (String(selectedVehicleId) !== nextId) setSelectedVehicleId(nextId);
	}, [vehicleIdParam, selectedVehicleId]); // intentionally NOT depending on selectedVehicleId

	useEffect(() => {
		if (!selectedVehicleId) return;

		const endpoint = `vehicles/${selectedVehicleId}/`;
		const fullUrl = new URL(endpoint, BACKEND_URL).toString();

		setLoadingDetails(true);
		fetch(fullUrl, {
			headers: { Authorization: `Bearer ${BACKEND_TOKEN}` },
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success) {
					setVehicleData({ ...data.data, tabs: data.data.tabs });
				} else {
					console.error("Details API error:", data.message);
				}
				setLoadingDetails(false);
			})
			.catch((err) => {
				console.error("Vehicle details fetch error:", err);
				setLoadingDetails(false);
			});
	}, [selectedVehicleId, detailsReloadKey]); // <-- added detailsReloadKey

	useLayoutEffect(() => {
		const hashTab = decodeURIComponent(
			(location.hash || "").replace(/^#/, "")
		);
		if (!hashTab || !vehicleData?.tabs?.length) return;
		const idx = vehicleData.tabs.findIndex(
			(t) => toSlug(t.name) === toSlug(hashTab) || t.name === hashTab
		);
		if (idx >= 0) setSelectedTab(idx);
	}, [location.hash, vehicleData?.tabs]);

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value); // no fetch here
	};

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") {
			// no fetch; filtering is client-side
			// optionally collapse search UI or give feedback, but nothing required
		}
	};

	const handleTabChange = (e, newValue) => {
		if (isMobile) setMobileTabsOpen(false);
		const tabName = vehicleData?.tabs?.[newValue]?.name || "Dashboard";
		if (selectedVehicleId) {
			navigate(
				`/vehicles/${String(selectedVehicleId)}#${encodeURIComponent(
					tabName
				)}`,
				{ replace: true }
			);
		}
	};

	const openTabByName = useCallback(
		(name) => {
			if (!vehicleData?.tabs?.length) return;
			const idx = vehicleData.tabs.findIndex((t) => t.name === name);
			if (idx < 0) return;
			setSelectedTab(idx);
			if (selectedVehicleId) {
				const tabName = vehicleData.tabs[idx].name;
				navigate(
					`/vehicles/${String(
						selectedVehicleId
					)}#${encodeURIComponent(tabName)}`,
					{ replace: true }
				);
			}
		},
		[vehicleData?.tabs, selectedVehicleId, navigate]
	);

	const renderMobileSearch = () => {
		if (isDealerView) {
			return (
				<Box
					sx={{
						p: 1,
						bgcolor: "background.paper",
						borderBottom: 1,
						borderColor: "divider",
						display: "flex",
						flexDirection: "column",
						gap: 1,
					}}
				>
					{renderPrimaryTabs("mobile")}
					<TextField
						placeholder="Search dealers"
						size="small"
						variant="outlined"
						fullWidth
						value={dealerSearchTerm}
						onChange={(e) => setDealerSearchTerm(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
							style: { borderRadius: 9999 },
						}}
					/>
					<Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
						{filteredDealers.length === 0 ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ p: 2, textAlign: "center" }}
							>
								No dealers found
							</Typography>
						) : (
								<List dense>
									{filteredDealers.map((dealer) => (
										<DealerRow
											key={dealer.id ?? dealer._id}
											dealer={dealer}
										selected={
											selectedDealerId != null &&
											String(selectedDealerId) ===
												String(dealer.id ?? dealer._id)
										}
											onSelect={handleDealerSelect(
												dealer.id ?? dealer._id
											)}
											onOpen={() =>
												navigate(
													`/dealers/${dealer.id ?? dealer._id}`,
													{ state: { dealer } }
												)
											}
										/>
									))}
								</List>
							)}
					</Box>
				</Box>
			);
		}

		const handleMobileSearch = (e) => {
			setSearchTerm(e.target.value);
			if (e.target.value.length > 0) setSearchMode(true);
		};
		const handleMobileSearchSubmit = (e) => {
			if (e.key === "Enter") setSearchMode(true);
		};

		const SearchBar = (
			<Box
				sx={{
					p: 1,
					bgcolor: "background.paper",
					boxShadow: searchMode ? 1 : 0,
					borderBottom: 1,
					borderColor: searchMode ? "divider" : "transparent",
					display: "flex",
					flexDirection: "column",
					gap: 1,
				}}
			>
				{renderPrimaryTabs("mobile")}

				{/* Dealer dropdown on its own row */}
				<FormControl
					size="small"
					fullWidth
					sx={{ position: "relative", zIndex: 1 }}
				>
					<InputLabel id="dealer-select-label">Dealer</InputLabel>
					{/* Mobile dealer select */}
					<Select
						labelId="dealer-select-label"
						label="Dealer"
						value={dealerId}
						onChange={(e) => setDealerId(e.target.value)}
					>
						<MenuItem value="ALL">All</MenuItem>
						{dealers.map((d) => (
							<MenuItem key={d._id} value={d._id}>
								{d._display}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{/* Row with hamburger  search  filter */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						position: "relative",
						zIndex: 2,
					}}
				>
					<IconButton
						size="small"
						aria-label="Open sections"
						onClick={() => setMobileTabsOpen(true)}
						sx={{ flexShrink: 0 }}
					>
						<MenuIcon fontSize="small" />
					</IconButton>

					<TextField
						placeholder="Search vehicles"
						size="small"
						variant="outlined"
						fullWidth
						sx={{ borderRadius: "9999px", bgcolor: "#f9f9f9" }}
						value={searchTerm}
						onChange={handleMobileSearch}
						onKeyDown={handleMobileSearchSubmit}
						onFocus={() => {
							setSearchMode(true);
							setSearchFocused(true);
						}}
						onBlur={() => setSearchFocused(false)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
							style: { borderRadius: 9999 },
						}}
					/>

					<Tooltip
						title={`Filter: ${filterLabel(statusFilter)} (${
							countsLegend[statusFilter] ?? 0
						})`}
					>
						<IconButton
							size="small"
							aria-label="Filter"
							onClick={openFilter}
						>
							{statusFilter === "ALL" ? (
								<FilterList fontSize="small" />
							) : (
								filterIconFor(statusFilter)
							)}
						</IconButton>
					</Tooltip>

					{renderFilterMenu(filterAnchor, closeFilter)}
				</Box>
			</Box>
		);

		// 2) when active, show full-screen overlay with results
		if (!searchMode) return SearchBar;

		return (
			<Box
				sx={{
					position: "fixed",
					inset: 0,
					zIndex: (t) => t.zIndex.modal,
					bgcolor: "background.paper",
					display: "flex",
					flexDirection: "column",
					height: "100dvh",
				}}
			>
				{SearchBar}
				<Divider sx={{ mt: 1 }} />
				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						px: 0,
						py: 0,
						position: "relative",
					}}
				>
					{/* Overlay close button that does NOT consume layout space */}
					<Box
						sx={{
							position: "sticky",
							top: 0,
							height: 0, // zero height so it doesn’t push content
							zIndex: (t) => t.zIndex.modal + 1,
							pointerEvents: "none", // allow clicks through except on the button
						}}
					>
						<IconButton
							aria-label="Close search"
							onClick={() => setSearchMode(false)}
							onMouseDown={(e) => e.preventDefault()}
							size="small"
							sx={{
								position: "absolute",
								right: 8,
								top: 8,
								pointerEvents: "auto",
								bgcolor: "background.paper",
								border: "1px solid",
								borderColor: "divider",
								boxShadow: 2,
								width: 32,
								height: 32,
								"&:hover": { bgcolor: "background.paper" },
							}}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>
					{loadingVehicles ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								mt: 2,
							}}
						>
							<CircularProgress size={24} />
						</Box>
					) : filteredVehicles.length === 0 ? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ p: 2, textAlign: "center" }}
						>
							No vehicles found
						</Typography>
					) : (
						<List dense sx={{ py: 0 }}>
							{filteredVehicles.map((v) => (
								<VehicleRow
									key={v.id}
									id={v.id}
									ch_no={v.ch_no}
									st={v.st}
									mdl_img_url={v.mdl_img_url}
									dlr_nm={v.dlr_nm}
									ct={v.ct}
									stt={v.stt}
									inst_date={v.inst_date}
									selected={
										selectedVehicleId != null &&
										v.id != null &&
										String(v.id) ===
											String(selectedVehicleId)
									}
									onSelect={makeSelectHandler(v.id)}
								/>
							))}
						</List>
					)}
				</Box>
			</Box>
		);
	};

	const renderSidebar = () => (
		<>
			{sidebarOpen ? (
				<Box
					sx={{
						width: 300,
						flex: "0 0 300px",
						flexShrink: 0,
						p: 2,
						bgcolor: "#fff",
						borderRight: "1px solid #e0e0e0",
						display: "flex",
						flexDirection: "column",
						borderRadius: 4,
						boxShadow: 2,
						m: 1,
						height: isMobile ? "auto" : "calc(100vh - 16px)",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							mb: 1,
						}}
					>
						<Typography variant="h6">
							{isDealerView
								? `Dealers (${filteredDealers.length})`
								: `Vehicles (${filteredVehicles.length})`}
						</Typography>
						<IconButton
							onClick={() => setSidebarOpen(false)}
							size="small"
						>
							<ChevronLeft />
						</IconButton>
					</Box>

					{renderPrimaryTabs()}

					{/* controls */}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							mb: 2,
						}}
					>
						{isDealerView ? (
							<TextField
								placeholder="Search dealers"
								size="small"
								variant="outlined"
								fullWidth
								value={dealerSearchTerm}
								onChange={(e) => setDealerSearchTerm(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon fontSize="small" />
										</InputAdornment>
									),
									style: { borderRadius: 9999 },
								}}
							/>
						) : (
							<>
								<FormControl size="small" fullWidth>
									<InputLabel id="dealer-select-label-d">
										Dealer
									</InputLabel>
									<Select
										labelId="dealer-select-label-d"
										label="Dealer"
										value={dealerId}
										onChange={(e) => setDealerId(e.target.value)}
									>
										<MenuItem value="ALL">All</MenuItem>
										{dealers.map((d) => (
											<MenuItem key={d._id} value={d._id}>
												{d._display}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									<TextField
										placeholder="Search vehicles"
										size="small"
										variant="outlined"
										fullWidth
										sx={{
											borderRadius: "9999px",
											bgcolor: "#f9f9f9",
											flexGrow: 1,
										}}
										value={searchTerm}
										onChange={handleSearchChange}
										onKeyDown={handleSearchSubmit}
										onFocus={() => setSearchFocused(true)}
										onBlur={() => setSearchFocused(false)}
										InputProps={{ style: { borderRadius: 9999 } }}
									/>
									<Tooltip
										title={`Filter: ${filterLabel(statusFilter)} (${
											countsLegend[statusFilter] ?? 0
										})`}
									>
										<IconButton
											size="small"
											aria-label="Filter"
											onClick={openFilter}
										>
											{statusFilter === "ALL" ? (
												<FilterList fontSize="small" />
											) : (
												filterIconFor(statusFilter)
											)}
										</IconButton>
									</Tooltip>
									{renderFilterMenu(filterAnchor, closeFilter)}
								</Box>
							</>
						)}
					</Box>

					<Box sx={{ overflowY: "auto", flexGrow: 1 }}>
						{isDealerView ? (
							filteredDealers.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ p: 2, textAlign: "center" }}
								>
									No dealers found
								</Typography>
							) : (
								<List dense>
									{filteredDealers.map((dealer) => {
										const key = dealer.id ?? dealer._id;
										return (
											<DealerRow
												key={key}
												dealer={dealer}
												selected={
													selectedDealerId != null &&
													String(selectedDealerId) ===
														String(key)
												}
												onSelect={handleDealerSelect(key)}
												onOpen={() =>
													navigate(`/dealers/${key}`, {
														state: { dealer },
													})
												}
											/>
										);
									})}
								</List>
							)
						) : loadingVehicles ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									mt: 2,
								}}
							>
								<CircularProgress size={24} />
							</Box>
						) : filteredVehicles.length === 0 ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ p: 2, textAlign: "center" }}
							>
								No vehicles found
							</Typography>
						) : (
							<List dense>
								{filteredVehicles.map((v) => (
									<VehicleRow
										key={v.id}
										id={v.id}
										ch_no={v.ch_no}
										st={v.st}
										mdl_img_url={v.mdl_img_url}
										dlr_nm={v.dlr_nm}
										ct={v.ct}
										stt={v.stt}
										inst_date={v.inst_date}
										selected={
											String(v.id) ===
											String(selectedVehicleId)
										}
										onSelect={makeSelectHandler(v.id)}
									/>
								))}
							</List>
						)}
					</Box>
				</Box>
			) : (
				<Box
					sx={{
						width: 40,
						flex: "0 0 40px",
						flexShrink: 0,
						bgcolor: "#fff",
						borderRight: "1px solid #e0e0e0",
						position: "relative",
						height: isMobile ? "auto" : "100vh",
						zIndex: 2,
					}}
				>
					<IconButton
						onClick={() => setSidebarOpen(true)}
						size="small"
						sx={{ position: "absolute", top: 16, right: 4 }}
					>
						<ChevronRight />
					</IconButton>
				</Box>
			)}
		</>
	);

	const renderVerticalTabs = () => (
		<Box sx={{ position: "relative", height: "100%" }}>
			<SwipeableDrawer
				sx={{ zIndex: (t) => t.zIndex.modal + 6 }} // above overlay and button
				open={mobileTabsOpen}
				onOpen={() => setMobileTabsOpen(true)}
				onClose={() => setMobileTabsOpen(false)}
				anchor="left"
				disableDiscovery={false}
				ModalProps={{ keepMounted: true }}
				PaperProps={{
					sx: {
						width: TAB_BAR_WIDTH,
						borderRight: 1,
						borderColor: "divider",
					},
				}}
			>
				<Toolbar
					sx={{ minHeight: 44, px: 1, justifyContent: "flex-start" }}
				>
					<Box
						component="img"
						src="/logo192.png"
						alt="Logo"
						sx={{ height: 28, width: 28 }}
					/>
				</Toolbar>
				<Divider />
				<Tabs
					value={selectedTab}
					onChange={handleTabChange}
					orientation="vertical"
					variant="scrollable"
					sx={{
						"& .MuiTabs-indicator": {
							width: 3,
							left: 0,
							bgcolor: "#1976d2",
							borderRadius: 2,
						},
						"& .MuiTab-root": {
							alignItems: "flex-start",
							justifyContent: "flex-start",
							textTransform: "none",
							fontWeight: 600,
							minHeight: 36, // was 40
							pl: 1,
							pr: 1, // tighter
							"&.Mui-selected": {
								bgcolor: "rgba(25,118,210,0.08)",
							},
						},
					}}
				>
					{vehicleData?.tabs.map((tab, idx) => (
						<Tab
							key={idx}
							disableRipple
							label={
								<TabLabel
									name={tab.name}
									selected={idx === selectedTab}
								/>
							}
						/>
					))}
				</Tabs>
			</SwipeableDrawer>

			<Box sx={{ height: "100%", p: 1 }}>
				{vehicleData?.tabs.map((tab, idx) => (
					<Box
						key={idx}
						sx={{
							display: idx === selectedTab ? "block" : "none",
							height: "100%",
						}}
					>
						{renderTabContentLazy(tab, idx === selectedTab)}
					</Box>
				))}
			</Box>
		</Box>
	);

	const renderHorizontalTabs = () => (
		<>
			<Tabs
				value={selectedTab}
				onChange={handleTabChange}
				variant="scrollable"
				scrollButtons="auto"
				sx={{
					borderBottom: 1,
					borderColor: "divider",
					mb: 0.5,
					px: 0.5, // tighter sides
					minHeight: 36, // trim bar height
					"& .MuiTabs-indicator": { height: 2, borderRadius: 1 },
					"& .MuiTab-root": {
						textTransform: "none",
						fontWeight: 500,
						minHeight: 36,
						minWidth: "auto", // <— kills the huge gap
						px: 0.75, // 6px left/right
						mx: 0.25, // small space between tabs
						fontSize: "0.9rem",
						"&.Mui-selected": { color: "text.primary" },
					},
				}}
			>
				{vehicleData?.tabs.map((tab, idx) => (
					<Tab
						key={idx}
						disableRipple
						label={
							<TabLabel
								name={tab.name}
								selected={idx === selectedTab}
							/>
						}
					/>
				))}
			</Tabs>

			{vehicleData?.tabs.map((tab, idx) => (
				<Box
					key={idx}
					sx={{ display: idx === selectedTab ? "block" : "none" }}
				>
					{renderTabContentLazy(tab, idx === selectedTab)}
				</Box>
			))}
		</>
	);

	const renderTabContentLazy = (tab, isActive) => {
		// Only mount heavy tabs when active
		if ((tab.name === "History" || tab.name === "Logs") && !isActive) {
			return null; // nothing mounted, nothing processed
		}
		return renderTabContent(tab);
	};

	const VehicleThumb = React.memo(
		function VehicleThumb({ src }) {
			return (
				<Avatar
					variant="rounded"
					src={src || undefined}
					imgProps={{ loading: "lazy", decoding: "async" }}
					sx={{
						width: 40,
						height: 40,
						mr: 1.25,
						bgcolor: "#fff",
						border: "1px solid #eee",
						flexShrink: 0,
					}}
				>
					{!src && (
						<DirectionsCar
							sx={{ fontSize: 22, color: "text.disabled" }}
						/>
					)}
				</Avatar>
			);
		},
		(prev, next) => prev.src === next.src
	);

	const VehicleRow = React.memo(
		function VehicleRow({
			id,
			ch_no,
			st,
			mdl_img_url,
			dlr_nm,
			ct,
			stt,
			inst_date,
			selected,
			onSelect, // stable from makeSelectHandler
		}) {
			return (
				<>
					<ListItem disableGutters sx={{ px: 0 }}>
						<ListItemButton
							selected={selected}
							onClick={onSelect}
							sx={{
								bgcolor: selected ? "#e0e0e0" : "transparent",
								borderRadius: 1,
								alignItems: "flex-start",
								py: 1,
							}}
						>
							<VehicleThumb src={mdl_img_url} />
							<Box sx={{ flexGrow: 1, minWidth: 0 }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										minWidth: 0,
									}}
								>
									<Typography
										variant="body2"
										fontWeight={700}
										noWrap
										title={ch_no || "NULL"}
										sx={{
											display: "block",
											maxWidth: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{ch_no || "NULL"}
									</Typography>
									<StatusIcon status={st} />
								</Box>

								{/* secondary */}
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 0.25,
										minWidth: 0,
									}}
								>
									{(dlr_nm || ct || stt) && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block" }}
										>
											{[
												dlr_nm,
												[ct, stt]
													.filter(Boolean)
													.join(", "),
											]
												.filter(Boolean)
												.join(" • ")}
										</Typography>
									)}
									{inst_date && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block" }}
										>
											Installed: {inst_date}
										</Typography>
									)}
								</Box>
							</Box>
						</ListItemButton>
					</ListItem>
					<Divider variant="middle" component="li" />
				</>
			);
		},
		(a, b) =>
			a.id === b.id &&
			a.ch_no === b.ch_no &&
			a.st === b.st &&
			a.mdl_img_url === b.mdl_img_url &&
			a.dlr_nm === b.dlr_nm &&
			a.ct === b.ct &&
			a.stt === b.stt &&
			a.inst_date === b.inst_date &&
			a.selected === b.selected
	);

	const DealerRow = React.memo(
		function DealerRow({ dealer, selected, onSelect, onOpen }) {
			const name = dealer?.name || "Dealer";
			const city = dealer?.city || "";
			const state = dealer?.state || "";
			const location = [city, state].filter(Boolean).join(", ");
			const fleet = dealer?.vehicle_count ?? 0;
			const formattedFleet = useMemo(
				() => new Intl.NumberFormat().format(fleet),
				[fleet]
			);

			return (
				<>
					<ListItem disableGutters sx={{ px: 0 }}>
						<ListItemButton
							selected={selected}
							onClick={onSelect}
							sx={{
								bgcolor: selected ? "#e8f4ff" : "transparent",
								borderRadius: 1,
								alignItems: "center",
								py: 1,
						}}
						>
							<Avatar
								sx={{
									width: 36,
									height: 36,
									mr: 1.25,
									bgcolor: selected ? "#1976d2" : "#f1f5f9",
									color: selected ? "#fff" : "text.secondary",
							}}
							>
								<Storefront fontSize="small" />
							</Avatar>
							<Box sx={{ flexGrow: 1, minWidth: 0 }}>
								<Typography
									variant="body2"
									fontWeight={700}
									noWrap
									title={name}
								>
									{name}
								</Typography>
								<Typography
									variant="caption"
									color="text.secondary"
									noWrap
									sx={{ display: "block" }}
								>
									{location || "—"}
								</Typography>
							</Box>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.75,
							}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 600, minWidth: 64, textAlign: "right" }}
								>
									{formattedFleet}
									{fleet === 1 ? " vehicle" : " vehicles"}
								</Typography>
								<IconButton
									size="small"
									onClick={(event) => {
										event.stopPropagation();
										if (typeof onOpen === "function") onOpen(event);
									}}
									sx={{
										border: "1px solid",
										borderRadius: 1.5,
										borderColor: "divider",
										color: "primary.main",
										p: 0.5,
									}}
								>
									<OpenInNew fontSize="inherit" />
								</IconButton>
							</Box>
						</ListItemButton>
					</ListItem>
					<Divider variant="middle" component="li" />
				</>
			);
		},
		(prev, next) =>
			prev.selected === next.selected &&
			prev.dealer?.id === next.dealer?.id &&
			prev.dealer?.vehicle_count === next.dealer?.vehicle_count &&
			prev.dealer?.name === next.dealer?.name &&
			prev.dealer?.city === next.dealer?.city &&
			prev.dealer?.state === next.dealer?.state
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: isMobile ? "column" : "row",
				height: isMobile ? "100dvh" : "100vh",
				bgcolor: "#f5f5f5",
			}}
		>
			{/* Mobile Layout */}
			{isMobile ? (
				<>
					{renderMobileSearch()}
					<Box
						sx={{
							flexGrow: 1,
							overflowY: "auto",
							m: 0, // was 1
							mt: 0, // was -2
							pt: 0, // was 2
						}}
					>
						{loadingDetails ? (
							<Box
								sx={{
									height: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<CircularProgress />
							</Box>
						) : selectedVehicleId ? (
							<Paper
								sx={{
									p: 1,
									borderRadius: 3,
									boxShadow: 2,
									minHeight: "100%",
								}}
							>
								<VehicleDataProvider
									key={selectedVehicleId || "none"}
									deviceId={selectedVehicle?.dev_id}
									chassisNum={selectedVehicle?.ch_no}
									selectedVehicle={selectedVehicle}
								>
									<SyncSelectedVehicle
										value={selectedVehicle}
									/>
									{renderVerticalTabs()}
								</VehicleDataProvider>
							</Paper>
						) : (
							<Paper sx={{ p: 0, borderRadius: 0, boxShadow: 0 }}>
								<Overview
									maps={maps}
									mode={primaryView}
									filter={isDealerView ? undefined : statusFilter}
									onFilterChange={
										isDealerView ? undefined : setStatusFilter
									}
									vehiclesMap={vehiclesMap}
									counts={countsLegend}
									search={searchTerm}
									dealerId={dealerId}
									dealers={filteredDealers}
									selectedDealerId={selectedDealerId}
									onDealerMarkerSelect={setSelectedDealerId}
								/>
							</Paper>
						)}
					</Box>
				</>
			) : (
				/* Desktop Layout */
				<>
					{renderSidebar()}
					<Box sx={{ flexGrow: 1, overflowY: "auto", m: 1 }}>
						{loadingDetails ? (
							<Box
								sx={{
									height: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<CircularProgress />
							</Box>
						) : selectedVehicleId ? (
							<Paper
								sx={{
									p: 1.5,
									borderRadius: 4,
									boxShadow: 3,
									minHeight: "100%",
								}}
							>
								<VehicleDataProvider
									key={selectedVehicle?.id || "none"}
									deviceId={selectedVehicle?.dev_id}
									chassisNum={selectedVehicle?.ch_no}
									selectedVehicle={selectedVehicle}
								>
									<SyncSelectedVehicle
										value={selectedVehicle}
									/>
									{renderHorizontalTabs()}
								</VehicleDataProvider>
							</Paper>
						) : (
							<Paper sx={{ p: 0, borderRadius: 0, boxShadow: 0 }}>
								<Overview
									maps={maps}
									mode={primaryView}
									filter={isDealerView ? undefined : statusFilter}
									onFilterChange={
										isDealerView ? undefined : setStatusFilter
									}
									vehiclesMap={vehiclesMap}
									counts={countsLegend}
									search={searchTerm}
									dealerId={dealerId}
									dealers={filteredDealers}
									selectedDealerId={selectedDealerId}
									onDealerMarkerSelect={setSelectedDealerId}
								/>
							</Paper>
						)}
					</Box>
				</>
			)}
		</Box>
	);

	function renderTabContent(tab) {
		const vehicle = selectedVehicle;
		if (!vehicle && selectedVehicleId) {
			return <Typography sx={{ p: 2 }}>Loading vehicle…</Typography>;
		}

		if (tab.name === "Dashboard" && selectedVehicleId) {
			return (
				<Dashboard
					deviceId={vehicle.dev_id}
					chassisNum={vehicle.ch_no}
					onAlertsClick={() => openTabByName("Alerts")}
				/>
			);
		}

		if (tab.name === "CAN" && selectedVehicleId) {
			if (vehicle) {
				return (
					<Box sx={{ mb: 2 }}>
						<CANData
							key={selectedVehicleId}
							deviceId={vehicle.dev_id}
							chassisNum={vehicle.ch_no}
						/>
					</Box>
				);
			}
		}

		if (tab.name === "Route" && selectedVehicleId) {
			if (vehicle) {
				return (
					<Box sx={{ mb: 2 }}>
						<Route
							deviceId={vehicle.dev_id}
							chassisNum={vehicle.ch_no}
						/>
					</Box>
				);
			}
		}

		if (tab.name === "Distance" && selectedVehicleId) {
			if (vehicle) {
				return (
					<Box sx={{ mb: 2 }}>
						<Distance
							deviceId={vehicle.dev_id}
							chassisNum={vehicle.ch_no}
						/>
					</Box>
				);
			}
		}

		if (tab.name === "Insights" && selectedVehicleId) {
			if (vehicle) {
				return (
					<Box sx={{ mb: 2 }}>
						<VehicleInsights
							vehicleId={selectedVehicleId}
							chassisNum={vehicle.ch_no}
						/>
					</Box>
				);
			}
		}

		if (tab.name === "History" && selectedVehicleId) {
			return (
				<History deviceId={vehicle.dev_id} chassisNum={vehicle.ch_no} />
			);
		}

		if (tab.name === "Logs" && selectedVehicleId) {
			return (
				<Logs
					isMobile={isMobile}
					vehicleId={selectedVehicleId}
					chassisNum={vehicle.ch_no}
				/>
			);
		}

		if (tab.name === "MQTT" && selectedVehicleId) {
			return (
				<MQTT deviceId={vehicle.dev_id} chassisNum={vehicle.ch_no} />
			);
		}

		if (tab.name === "Commands" && selectedVehicleId) {
			if (vehicle) {
				return (
					<Box sx={{ mb: 2 }}>
						<Commands deviceId={vehicle.dev_id} />
					</Box>
				);
			}
		}

		if (tab.name === "Alerts" && selectedVehicleId) {
			return (
				<Alerts
					deviceId={vehicle.dev_id}
					chassisNumber={vehicle.ch_no}
				></Alerts>
			);
		}

		if (tab.tab_content?.length > 0) {
			return tab.tab_content.map((item, idx) => {
				if (item.type === "map") {
					return (
						<Box key={idx} sx={{ mb: 2 }}>
							<Typography variant="subtitle1">
								{item.name}
							</Typography>
							{item.data?.map((loc, i) => (
								<Typography key={i} variant="body2">
									Latitude: {loc.latitude}, Longitude:{" "}
									{loc.longitude}{" "}
									{loc.current_location
										? "(Current Location)"
										: ""}
								</Typography>
							))}
						</Box>
					);
				} else if (item.type === "display") {
					return (
						<Paper
							key={idx}
							elevation={1}
							sx={{
								p: 1.25,
								mb: 1.5,
								borderRadius: 1,
								bgcolor: "#f9f9f9",
								display: "flex",
								justifyContent: "space-between",
								flexDirection:
									isMobile && item.value?.length > 15
										? "column"
										: "row",
								alignItems:
									isMobile && item.value?.length > 15
										? "flex-start"
										: "center",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
							}}
						>
							<Typography
								variant="body2"
								sx={{ fontWeight: 500, color: "#555" }}
							>
								{item.name}
							</Typography>
							<Typography
								variant="subtitle1"
								sx={{
									fontWeight: 600,
									color: "#1976d2",
									wordBreak: "break-word",
									textAlign: isMobile ? "left" : "right",
									mt:
										isMobile && item.value?.length > 15
											? 1
											: 0,
								}}
							>
								{item.value}
							</Typography>
						</Paper>
					);
				} else {
					return (
						<Box key={idx} sx={{ mb: 1 }}>
							<Typography variant="subtitle2">
								{item.name}:
							</Typography>
							<Typography variant="body1">
								{JSON.stringify(item)}
							</Typography>
						</Box>
					);
				}
			});
		}

		if (tab.table_content && tab.headers?.length > 0) {
			return (
				<TableContainer
					component={Paper}
					elevation={1}
					sx={{
						mt: 2,
						overflow: "auto",
						maxWidth: "100%",
					}}
				>
					<Table>
						<TableHead>
							<TableRow>
								{tab.headers.map((header, i) => (
									<TableCell key={i}>
										<strong>{header.label}</strong>
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{tab.table_content.map((row, rowIdx) => (
								<TableRow key={rowIdx}>
									{tab.headers.map((header, colIdx) => (
										<TableCell key={colIdx}>
											{row[header.id]}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			);
		}

		return <Typography>No content available.</Typography>;
	}
};

export default Vehicles;
